<template>
  <div class="account-selection-wizard">
    <v-dialog
      v-model="dialogVisible"
      persistent
      :max-width="(currentStep === 2 && selectedAccountType === 'Record Label') || currentStep === 4 ? '720' : '600'"
      content-class="account_selection_wizard_modal"
    >
      <div :class="{'dimmed-dialog': wizardPaused}">
        <!-- Overlay for dimming when subscription dialog is shown -->
        <div class="dialog-overlay" v-if="wizardPaused"></div>
        <!-- Wizard Header - Styled like structure_header -->
        <div class="wizard-header">
          <h2 class="heading_wizard">
            {{ 
              currentStep === 1 ? 'Account Selection' : 
              currentStep === 2 ? 'Choose Structure' : 
              (currentStep === 3 && selectedStructure === 'Single Artist Release') || currentStep === 5 ? 'Select Primary Artist' : 
              'Choose Accounts' 
            }}
          </h2>
          <button class="close_modal" @click="closeWizard"></button>
        </div>

        <!-- Step 1: Account Type Selection -->
        <div class="wrapper_content">
          <div v-if="currentStep === 1" class="wizard-content">
            <!-- Horizontal Options -->
            <div class="horizontal-options">
              <!-- Record Label Option -->
              <div 
                class="account-option" 
                :class="{'selected': selectedAccountType === 'Record Label'}" 
                @click="selectAccountType('Record Label')"
              >
                <div class="option-type">
                  Record Label
                  <span class="option-desc">Manage music releases for your label and associated artists</span>
                </div>
                <div class="option-indicator">
                  <v-radio 
                    v-if="selectedAccountType === 'Record Label'" 
                    v-model="selectedAccountType" 
                    value="Record Label" 
                    color="#20C4F5" 
                    :ripple="false" 
                    class="hidden-radio"
                  ></v-radio>
                </div>
              </div>

              <!-- Artist Account Option -->
              <div 
                class="account-option" 
                :class="{'selected': selectedAccountType === 'Artist Account'}" 
                @click="selectAccountType('Artist Account')"
              >
                <div class="option-type">
                  Artist Account
                  <span class="option-desc">Manage your personal music releases and collaborations</span>
                </div>
                <div class="option-indicator">
                  <v-radio 
                    v-if="selectedAccountType === 'Artist Account'" 
                    v-model="selectedAccountType" 
                    value="Artist Account" 
                    color="#20C4F5" 
                    :ripple="false" 
                    class="hidden-radio"
                  ></v-radio>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: Structure Selection (shown for both Artist Account and Record Label) -->
          <div v-if="currentStep === 2" class="wizard-content structure-content">
            <!-- Custom horizontal layout for structure options -->
            <div class="horizontal_options">
              <!-- One Artist Option -->
              <div class="structure_option" :class="{'selected': selectedStructure === 'Single Artist Release'}" @click="selectStructure('Single Artist Release')">
                <div class="option_type">
                  Single Artist Release
                  <span class="option_desc">One main artist and collaboration on tracks</span>
                </div>
                <div class="option_content">
                  <div class="cover_preview">
                    <div class="cover_box"></div>
                    <div class="preview_artist">Artist A</div>
                  </div>
                </div>
                <!-- Only show radio when structure is selected to avoid default selection -->
                <v-radio v-if="selectedStructure === 'Single Artist Release'" 
                         v-model="selectedStructure" 
                         value="Single Artist Release" 
                         color="#20C4F5" 
                         :ripple="false" 
                         class="hidden_radio"></v-radio>
              </div>

              <!-- Collaboration Option -->
              <div class="structure_option" :class="{'selected': selectedStructure === 'Multi-Artist Release'}" @click="selectStructure('Multi-Artist Release')">
                <div class="option_type">
                  Multi-Artist Release
                  <span class="option_desc">More than one artist collaborating on a release</span>
                </div>
                <div class="option_content">
                  <div class="cover_preview">
                    <div class="cover_box"></div>
                    <div class="preview_artist">Artist A & Artist B</div>
                  </div>
                </div>
                <!-- Only show radio when structure is selected to avoid default selection -->
                <v-radio v-if="selectedStructure === 'Multi-Artist Release'" 
                         v-model="selectedStructure" 
                         value="Multi-Artist Release" 
                         color="#20C4F5" 
                         :ripple="false" 
                         class="hidden_radio"></v-radio>
              </div>
              
              <!-- Compilation Option (only shown for Record Label) -->
              <div v-if="selectedAccountType === 'Record Label'" class="structure_option" :class="{'selected': selectedStructure === 'Various Artists Release'}" @click="selectStructure('Various Artists Release')">
                <div class="option_type">
                  Various Artists Release
                  <span class="option_desc">A collection of tracks from different artists</span>
                </div>
                <div class="option_content">
                  <div class="cover_preview">
                    <div class="cover_box"></div>
                    <div class="preview_artist">Various Artists</div>
                  </div>
                </div>
                <!-- Only show radio when structure is selected to avoid default selection -->
                <v-radio v-if="selectedStructure === 'Various Artists Release'" 
                         v-model="selectedStructure" 
                         value="Various Artists Release" 
                         color="#20C4F5" 
                         :ripple="false" 
                         class="hidden_radio"></v-radio>
              </div>
            </div>
          </div>
          
          <!-- Consolidated Artist Selection (used for both single and multi-artist flows) -->
          <div v-if="(currentStep === 3 && selectedStructure === 'Single Artist Release') || currentStep === 5" class="wizard-content">
            <!-- Create New Account Section -->
            <div class="box_create_button" @click="openArtistCreationDialog" :class="{'active': showCreateAccountBox}">
              <button type="button" :class="{'active': showCreateAccountBox}"></button>
              <div>
                Create new account
                <span>Click here to create a new artist account</span>
              </div>
            </div>
            
            <!-- Loading State -->
            <div v-if="isLoadingArtists" class="list_accounts primary-artist-list">
              <div class="list-state-container">
                <v-progress-circular :size="40" :width="3" color="#20C4F5" indeterminate></v-progress-circular>
                <span class="loading-message">Loading your artists...</span>
              </div>
            </div>
            
            <!-- Artist List Section -->
            <div v-else-if="userArtists && userArtists.length" class="list_accounts primary-artist-list">
              <div class="item_account" v-for="artist in userArtists" :key="artist.id" @click="selectPrimaryArtist(artist)">
                <div class="data">
                  <div class="img">
                    <img v-if="artist.avatar && artist.avatar.thumbnail"
                         :src="`${artist.avatar.thumbnail['50x50']}?cache=${new Date().getTime()}`"
                         :srcset="`${artist.avatar.thumbnail['50x50']}?cache=${new Date().getTime()} 1x, ${artist.avatar.thumbnail['50x50@2x']}?cache=${new Date().getTime()} 2x`"
                         :alt="artist.name">
                    <div class="default_user_avatar" v-else>
                      <img :src="require('@/assets/image/svg/square_avatar.svg')" alt="Default avatar">
                    </div>
                  </div>
                  <div class="description">
                    <div class="name">{{ artist.name }}</div>
                    <div class="type" v-if="artist.hasActiveSubscription === true">{{ artist['@type'] }}</div>
                    <SubscriptionStatus v-if="hasRestrictedStatus(artist)" :subscription="artist.subscription"/>
                  </div>
                </div>
                <div class="action">
                  <div class="check_round" :class="{checked: primaryArtist && primaryArtist.id === artist.id}"></div>
                </div>
              </div>
            </div>
            <!-- Empty State -->
            <div v-else class="list_accounts primary-artist-list">
              <div class="list-state-container">
                <div class="empty-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" stroke="#7481A6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M20.5899 22C20.5899 18.13 16.7399 15 11.9999 15C7.25991 15 3.40991 18.13 3.40991 22" stroke="#7481A6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <span class="empty-message">No artist accounts found</span>
                <button class="create-btn" @click="openArtistCreationDialog">
                  Create new artist
                </button>
              </div>
            </div>
          </div>
          
          <!-- Step 4: Multi-Artist Selection Options (only shown when Multi-Artist Release is selected) -->
          <div v-if="currentStep === 4" class="wizard-content">
            
            <!-- Horizontal Options for Artist Selection -->
            <div class="horizontal-options">
              <!-- Record Label Option (only shown for Record Label flow) -->
              <div 
                v-if="selectedAccountType === 'Record Label'" 
                class="account-option" 
                :class="{
                  'has-artist': selectedLabel, 
                  'hover-only': true
                }" 
                @click="selectArtistOption('Record Label')"
              >
                <div class="option-type">
                  <span class="option-number" :class="{ 'pulsing-number': !selectedLabel, 'completed': selectedLabel }">
                    <span v-if="!selectedLabel">1</span>
                    <span v-else class="checkmark"></span>
                  </span>
                  <div class="option-title">Record Label</div>
                  <span class="option-desc" v-if="!selectedLabel">Select the record label for this release</span>
                  <div class="selected-artist" v-if="selectedLabel">
                    <div class="artist-avatar">
                      <img v-if="selectedLabel.logo && selectedLabel.logo.thumbnail"
                           :src="`${selectedLabel.logo.thumbnail['50x50']}?cache=${new Date().getTime()}`"
                           :alt="selectedLabel.title">
                      <div class="default_user_avatar" v-else></div>
                    </div>
                    <div class="artist-info">
                      <div class="artist-name">{{ selectedLabel.title }}</div>
                      <div class="artist-type">Record Label</div>
                    </div>
                    <div class="clear-button" @click.stop="clearSelectedLabel">
                      <span class="close-x">×</span>
                    </div>
                  </div>
                </div>
                <div class="option-indicator">
                  <v-radio 
                    v-if="selectedArtistOption === 'Record Label'" 
                    v-model="selectedArtistOption" 
                    value="Record Label" 
                    color="#20C4F5" 
                    :ripple="false" 
                    class="hidden-radio"
                  ></v-radio>
                </div>
              </div>
              
              <!-- Primary Artist Option -->
              <div 
                class="account-option" 
                :class="{
                  'has-artist': primaryArtist, 
                  'hover-only': selectedStructure === 'Multi-Artist Release'
                }" 
                @click="selectArtistOption('Primary Artist')"
              >
                <div class="option-type">
                  <span class="option-number" :class="{ 'pulsing-number': selectedStructure === 'Multi-Artist Release' && !primaryArtist, 'completed': primaryArtist }">
                    <span v-if="!primaryArtist">{{ selectedAccountType === 'Record Label' ? '2' : '1' }}</span>
                    <span v-else class="checkmark"></span>
                  </span>
                  <div class="option-title">Primary Artist</div>
                  <span class="option-desc" v-if="!primaryArtist">Select the main artist for this release</span>
                  <div class="selected-artist" v-if="primaryArtist">
                    <div class="artist-avatar">
                      <img v-if="primaryArtist.avatar && primaryArtist.avatar.thumbnail"
                           :src="`${primaryArtist.avatar.thumbnail['50x50']}?cache=${new Date().getTime()}`"
                           :alt="primaryArtist.name">
                      <div class="default_user_avatar" v-else></div>
                    </div>
                    <div class="artist-info">
                      <div class="artist-name">{{ primaryArtist.name }}</div>
                      <div class="artist-type" v-if="primaryArtist.hasActiveSubscription === true">{{ primaryArtist['@type'] }}</div>
                    </div>
                    <div class="clear-button" @click.stop="clearPrimaryArtist">
                      <span class="close-x">×</span>
                    </div>
                  </div>
                </div>
                <div class="option-indicator">
                  <v-radio 
                    v-if="selectedArtistOption === 'Primary Artist'" 
                    v-model="selectedArtistOption" 
                    value="Primary Artist" 
                    color="#20C4F5" 
                    :ripple="false" 
                    class="hidden-radio"
                  ></v-radio>
                </div>
              </div>

              <!-- Secondary Artists Option -->
              <div 
                class="account-option" 
                :class="{
                  'has-artist': secondaryArtists && secondaryArtists.length > 0, 
                  'hover-only': selectedStructure === 'Multi-Artist Release',
                  'disabled': !primaryArtist,
                  'show-tooltip': !primaryArtist
                }" 
                @click="primaryArtist ? selectArtistOption('Secondary Artists') : null"
              >
                <div class="option-type">
                  <span 
                    class="option-number" 
                    :class="{ 
                      'pulsing-number': selectedStructure === 'Multi-Artist Release' && primaryArtist && (!secondaryArtists || secondaryArtists.length === 0),
                      'completed': secondaryArtists && secondaryArtists.length > 0
                    }"
                  >
                    <span v-if="!secondaryArtists || secondaryArtists.length === 0">{{ selectedAccountType === 'Record Label' ? '3' : '2' }}</span>
                    <span v-else class="checkmark"></span>
                  </span>
                  <div class="option-title">Secondary Artists</div>
                  <div class="tooltip" v-if="!primaryArtist">Please select a primary artist first</div>
                  <span class="option-desc" v-if="!secondaryArtists || secondaryArtists.length === 0">Select additional artists collaborating on this release</span>
                  
                  <!-- Display selected secondary artists -->
                  <div class="selected-artist" v-if="secondaryArtists && secondaryArtists.length > 0">
                    <div class="artist-info">
                      <div class="artist-name" v-if="secondaryArtists.length === 1">{{ secondaryArtists[0].name }}</div>
                      <div class="artist-name" v-else>{{ secondaryArtists.length }} artists selected</div>
                      <div class="artist-type">Secondary Artists</div>
                    </div>
                    <div class="clear-button" @click.stop="clearSecondaryArtists">
                      <span class="close-x">×</span>
                    </div>
                  </div>
                </div>
                <div class="option-indicator">
                  <v-radio 
                    v-if="selectedArtistOption === 'Secondary Artists'" 
                    v-model="selectedArtistOption" 
                    value="Secondary Artists" 
                    color="#20C4F5" 
                    :ripple="false" 
                    class="hidden-radio"
                  ></v-radio>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Step 6: Record Label + Artist Selection (only shown for Record Label with Single Artist Release) -->
          <div v-if="currentStep === 6" class="wizard-content">
            <!-- Horizontal Options for Record Label/Primary Artist Selection -->
            <div class="horizontal-options">
              <!-- Record Label Option -->
              <div 
                class="account-option" 
                :class="{
                  'has-artist': selectedLabel, 
                  'hover-only': true
                }" 
                @click="selectArtistOption('Record Label')"
              >
                <div class="option-type">
                  <span class="option-number" :class="{ 'pulsing-number': !selectedLabel, 'completed': selectedLabel }">
                    <span v-if="!selectedLabel">1</span>
                    <span v-else class="checkmark"></span>
                  </span>
                  <div class="option-title">Record Label</div>
                  <span class="option-desc" v-if="!selectedLabel">Select the record label for this release</span>
                  <div class="selected-artist" v-if="selectedLabel">
                    <div class="artist-avatar">
                      <img v-if="selectedLabel.logo && selectedLabel.logo.thumbnail"
                           :src="`${selectedLabel.logo.thumbnail['50x50']}?cache=${new Date().getTime()}`"
                           :alt="selectedLabel.title">
                      <div class="default_user_avatar" v-else></div>
                    </div>
                    <div class="artist-info">
                      <div class="artist-name">{{ selectedLabel.title }}</div>
                      <div class="artist-type">Record Label</div>
                    </div>
                    <div class="clear-button" @click.stop="clearSelectedLabel">
                      <span class="close-x">×</span>
                    </div>
                  </div>
                </div>
                <div class="option-indicator">
                  <v-radio 
                    v-if="selectedArtistOption === 'Record Label'" 
                    v-model="selectedArtistOption" 
                    value="Record Label" 
                    color="#20C4F5" 
                    :ripple="false" 
                    class="hidden-radio"
                  ></v-radio>
                </div>
              </div>

              <!-- Primary Artist Option -->
              <div 
                class="account-option" 
                :class="{
                  'has-artist': primaryArtist, 
                  'hover-only': true,
                  'disabled': !selectedLabel,
                  'show-tooltip': !selectedLabel
                }" 
                @click="selectedLabel ? selectArtistOption('Primary Artist') : null"
              >
                <div class="option-type">
                  <span 
                    class="option-number" 
                    :class="{ 'pulsing-number': selectedLabel && !primaryArtist, 'completed': primaryArtist }"
                  >
                    <span v-if="!primaryArtist">2</span>
                    <span v-else class="checkmark"></span>
                  </span>
                  <div class="option-title">Primary Artist</div>
                  <div class="tooltip" v-if="!selectedLabel">Please select a record label first</div>
                  <span class="option-desc" v-if="!primaryArtist">Select the main artist for this release</span>
                  
                  <!-- Display selected primary artist -->
                  <div class="selected-artist" v-if="primaryArtist">
                    <div class="artist-avatar">
                      <img v-if="primaryArtist.avatar && primaryArtist.avatar.thumbnail"
                           :src="`${primaryArtist.avatar.thumbnail['50x50']}?cache=${new Date().getTime()}`"
                           :alt="primaryArtist.name">
                      <div class="default_user_avatar" v-else></div>
                    </div>
                    <div class="artist-info">
                      <div class="artist-name">{{ primaryArtist.name }}</div>
                      <div class="artist-type" v-if="primaryArtist.hasActiveSubscription === true">{{ primaryArtist['@type'] }}</div>
                    </div>
                    <div class="clear-button" @click.stop="clearPrimaryArtist">
                      <span class="close-x">×</span>
                    </div>
                  </div>
                </div>
                <div class="option-indicator">
                  <v-radio 
                    v-if="selectedArtistOption === 'Primary Artist'" 
                    v-model="selectedArtistOption" 
                    value="Primary Artist" 
                    color="#20C4F5" 
                    :ripple="false" 
                    class="hidden-radio"
                  ></v-radio>
                </div>
              </div>
            </div>
          </div>

        </div>
        
        <!-- Wizard Actions -->
        <div class="wrapper_button center">
          <button type="button" class="tetriatary_btn" @click="previousStep" v-ripple v-if="currentStep > 1">Back</button>
          <button type="button" class="tetriatary_btn" @click="closeWizard" v-ripple v-if="currentStep === 1">Cancel</button>
          <button 
            type="button" 
            class="tetriatary_btn" 
            @click="nextStep" 
            :disabled="(currentStep === 1 && !selectedAccountType) || 
                      (currentStep === 2 && !selectedStructure) || 
                      (currentStep === 3 && selectedStructure === 'Single Artist Release' && !primaryArtist) ||
                      (currentStep === 4 && selectedStructure === 'Multi-Artist Release' && (!primaryArtist || !secondaryArtists.length)) ||
                      (currentStep === 5 && !primaryArtist) ||
                      (currentStep === 6 && (!selectedLabel || !primaryArtist))" 
            v-ripple
          >
            {{ (currentStep === 3 && selectedStructure === 'Single Artist Release') || 
               (currentStep === 4 && selectedStructure === 'Multi-Artist Release') ||
               (currentStep === 6) ? 'Finish' : 'Next' }}
          </button>
        </div>
      </div>
    </v-dialog>
    
    <!-- Creator Account Renewal Dialog -->
    <creator-account-renewal-dialog
      v-model="showSubscriptionRenewalDialog"
      :account="primaryArtist"
      @cancel="onRenewalCancel"
      @success="onRenewalSuccess"
    />
    
    <!-- Account Creation Dialog -->
    <account-creation-dialog
      v-model="showArtistCreationDialog"
      :account-type="selectedAccountType === 'Artist Account' ? 'Artist' : 'Label'"
      @cancel="onArtistCreationCancel"
      @created="onAccountCreated"
    />
    
    <!-- Secondary Artists Selector Dialog -->
    <secondary-artists-selector
      v-if="selectedStructure === 'Multi-Artist Release'"
      v-model="showSecondaryArtistsDialog"
      :selected-artists="secondaryArtists"
      :primary-artist="primaryArtist"
      @selection-confirmed="onSecondaryArtistsSelected"
      @dialog-closed="onSecondaryArtistsDialogClosed"
    />
    
    <!-- Record Label Selector Dialog -->
    <record-label-selector
      v-model="showRecordLabelDialog"
      :initial-selected-label="selectedLabel || SELECTED_LABEL"
      :is-various-artists-flow="selectedStructure === 'Various Artists Release'"
      @label-selected="onRecordLabelSelected"
      @dialog-closed="onRecordLabelDialogClosed"
    />
    
    <!-- ReleaseDialogs component for various warnings and confirmations -->
    <release-dialogs
      ref="releaseDialogs"
      @confirm-move-artist="handleConfirmMoveArtist"
      @cancel-move-artist="handleCancelMoveArtist"
      @confirm-clear-label="handleConfirmClearLabel"
      @cancel-clear-label="handleCancelClearLabel"
      @confirm-clear-secondary-artists="confirmClearSecondaryArtists"
      @cancel-clear-secondary-artists="cancelClearSecondaryArtists"
      @confirm-clear-artists="confirmClearArtists"
      @cancel-clear-artists="cancelClearArtists"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import SubscriptionStatus from '@/components/subscriptionStatus.vue';
import RecordLabelSelector from './RecordLabelSelector.vue';
import CreatorAccountRenewalDialog from './CreatorAccountRenewalDialog.vue';
import AccountCreationDialog from './AccountCreationDialog.vue';
import SecondaryArtistsSelector from './SecondaryArtistsSelector.vue';
import EmptyArtistsList from './EmptyArtistsList.vue';
import ReleaseDialogs from './ReleaseDialogs.vue';

export default {
  name: 'AccountSelectionWizard',
  components: {
    SubscriptionStatus,
    CreatorAccountRenewalDialog,
    AccountCreationDialog,
    SecondaryArtistsSelector,
    RecordLabelSelector,
    EmptyArtistsList,
    ReleaseDialogs
  },
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      pendingArtistAccountNavigation: false,
      pendingStructureChange: false,
      pendingVariousArtistsChange: false,
      currentStep: 1,
      selectedAccountType: null,
      selectedStructure: null,
      selectedArtistOption: null, // For multi-artist release - tracks which option is selected (Primary or Secondary)
      primaryArtist: null, // The selected artist for both single and multi-artist releases
      secondaryArtists: [], // For multi-artist release - the selected secondary artists
      selectedLabel: null,
      showCreateAccountBox: false,
      userArtists: [], // Will be populated with the user's artist accounts
      isLoadingArtists: false, // Flag to indicate if artists are being loaded
      showSubscriptionRenewalDialog: false,
      showArtistCreationDialog: false,
      showSecondaryArtistsDialog: false, // Flag for showing the secondary artists selector
      showRecordLabelDialog: false,
      wizardPaused: false, // Flag to indicate if the wizard flow is paused for subscription renewal or artist creation
      tempArtistSelection: null, // Temporary storage for artist being selected as primary
      previewedFlowType: null,
      previewedStructure: null
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    ...mapGetters({
      YOUR_ARTISTS: 'YOUR_ARTISTS',
      subscriptionClientSecret: 'SUBSCRIPTION_CLIENT_SECRET',
      userID: 'userID',
      PRIMARY_ARTIST: 'PRIMARY_ARTIST',
      RELEASE_STRUCTURE: 'RELEASE_STRUCTURE',
      storeSecondaryArtists: 'secondaryArtists',
      SELECTED_LABEL: 'SELECTED_LABEL'
    })
  },
  watch: {
    wizardPaused(newVal) {
      this.$emit('wizard-pause-state-changed', newVal);
    },
    // Watch for changes to the selected artist in the store
    PRIMARY_ARTIST(newArtist, oldArtist) {
      console.log('PRIMARY_ARTIST changed:', newArtist, 'Structure:', this.selectedStructure, 'Primary Artist:', this.primaryArtist);
      
      // If the artist was cleared in ReleaseInfo (newArtist is null) and we had a primary artist
      if (newArtist === null && this.primaryArtist !== null) {
        console.log('Artist was cleared in ReleaseInfo, clearing primaryArtist');
        this.primaryArtist = null;
        return;
      }
      
      // If we're in the multi-artist flow
      if (this.selectedStructure === 'Multi-Artist Release') {
        // If an artist was selected and we're in the primary artist flow
        if (newArtist !== null && (this.selectedArtistOption === 'Primary Artist' || this.primaryArtist)) {
          console.log('Syncing primaryArtist with store:', newArtist);
          this.primaryArtist = newArtist;
        }
      }
    },
    // Watch for changes to the release structure in the store
    RELEASE_STRUCTURE(newStructure, oldStructure) {
      console.log('RELEASE_STRUCTURE changed in Vuex store:', newStructure, 'Current local structure:', this.selectedStructure);
      
      // If the structure was changed in ReleaseInfo and it's different from our local structure
      if (newStructure !== this.selectedStructure) {
        console.log('Syncing selectedStructure with Vuex store:', newStructure);
        this.selectedStructure = newStructure;
      }
    },
    // Watch for changes to the secondary artists in the store
    storeSecondaryArtists(newArtists) {
      console.log('storeSecondaryArtists changed:', newArtists);
      
      // Update the local secondaryArtists array with the store value
      if (JSON.stringify(this.secondaryArtists) !== JSON.stringify(newArtists)) {
        console.log('Syncing secondaryArtists with store');
        this.secondaryArtists = [...newArtists];
      }
    },
    currentStep(newStep) {
      // When moving to step 3, load the user's artists
      if (newStep === 3 && this.selectedStructure === 'Single Artist Release') {
        this.loadUserArtists();
      }
    },
    // Watch for dialog opening to ensure fresh data and restore saved state
    dialogVisible(newVal) {
      if (newVal) {
        // When the dialog is opened, load the user's artists
        this.loadUserArtists();
        
        // Restore any previous state (structure, artists, etc.)
        this.restoreWizardState();
        
        console.log('Dialog opened, loaded artists and restored state');
      }
    },
    // Watch for changes to the selected label in the store
    SELECTED_LABEL(newLabel, oldLabel) {
      console.log('SELECTED_LABEL changed:', newLabel);
      
      // If the label was cleared in ReleaseInfo (newLabel is null) and we had a selected label
      if (newLabel === null && this.selectedLabel !== null) {
        console.log('Label was cleared in ReleaseInfo, clearing selectedLabel');
        this.selectedLabel = null;
        return;
      }
      
      // If we're in the Record Label flow with Single Artist Release
      if (this.selectedAccountType === 'Record Label' && this.selectedStructure === 'Single Artist Release') {
        // If a label was selected and we're in the Record Label option
        if (newLabel !== null && (this.selectedArtistOption === 'Record Label' || this.selectedLabel === null)) {
          console.log('Syncing selectedLabel with store:', newLabel);
          this.selectedLabel = newLabel;
        }
      }
    }
  },
  methods: {
    /**
     * Handle confirmation of clearing the label when switching to Artist Account flow
     */
    handleConfirmClearLabel() {
      console.log('handleConfirmClearLabel called');
      // Make sure the dialog is closed
      this.$refs.releaseDialogs.clearLabelDialogVisible = false;
      // Undim the wizard background
      this.wizardPaused = false;
      
      if (this.pendingArtistAccountNavigation) {
        // Check if we had Various Artists structure selected
        const hadVariousArtistsSelected = this.selectedStructure === 'Various Artists Release';
        
        // Clear local state
        this.selectedLabel = null;
        // Clear Vuex store
        this.$store.commit('SET_SELECTED_LABEL', null);
        console.log('Cleared record label selection after confirmation');
        
        // If we had Various Artists Release selected, clear it since it's not valid for Artist Account flow
        if (hadVariousArtistsSelected) {
          console.log('Also clearing Various Artists structure since switching to Artist Account flow');
          this.selectedStructure = null;
          this.previewedStructure = null;
          
          // Emit structure reset event
          this.$emit('structure-preview-changed', null);
          
          // Update Vuex store to clear the structure
          this.$store.commit('SET_RELEASE_STRUCTURE', null);
        }
        
        // Continue to step 2
        this.currentStep = 2;
        // Reset the pending flag
        this.pendingArtistAccountNavigation = false;
      }
    },
    
    /**
     * Handle cancellation of clearing the label
     */
    handleCancelClearLabel() {
      console.log('handleCancelClearLabel called');
      // Make sure the dialog is closed
      this.$refs.releaseDialogs.clearLabelDialogVisible = false;
      // Undim the wizard background
      this.wizardPaused = false;
      
      if (this.pendingArtistAccountNavigation) {
        // Switch back to Record Label account type
        this.selectedAccountType = 'Record Label';
        console.log('Reverted to Record Label account type after cancellation');
        // Reset the pending flag
        this.pendingArtistAccountNavigation = false;
      }
    },
    
    /**
     * Handle confirmation of clearing secondary artists when switching from Multi-Artist to Single Artist structure
     */
    confirmClearSecondaryArtists() {
      console.log('confirmClearSecondaryArtists called');
      // Clear the secondary artists
      this.secondaryArtists = [];
      this.$store.commit('SET_SECONDARY_ARTISTS', []);
      // Undim the wizard background
      this.wizardPaused = false;
      
      if (this.pendingStructureChange) {
        // Navigate differently based on account type
        if (this.selectedAccountType === 'Record Label') {
          // For Record Label flow, go to step 6 (which shows both label and artist selectors)
          this.currentStep = 6;
          console.log('Navigating to Record Label + Artist selection page (step 6)');
        } else {
          // For Artist Account flow, continue to step 3 (primary artist selection)
          this.currentStep = 3;
          console.log('Navigating to Artist selection page (step 3)');
        }
        // Reset the pending flag
        this.pendingStructureChange = false;
      }
    },
    
    /**
     * Handle cancellation of clearing secondary artists
     */
    cancelClearSecondaryArtists() {
      console.log('cancelClearSecondaryArtists called');
      // Make sure the dialog is closed
      this.$refs.releaseDialogs.clearSecondaryArtistsDialogVisible = false;
      // Undim the wizard background
      this.wizardPaused = false;
      
      if (this.pendingStructureChange) {
        // Switch back to Multi-Artist Release structure
        this.selectedStructure = 'Multi-Artist Release';
        console.log('Reverted to Multi-Artist Release structure after cancellation');
        // Reset the pending flag
        this.pendingStructureChange = false;
      }
    },
    
    /**
     * Handle confirmation of clearing all artists when switching to Various Artists Release
     */
    confirmClearArtists() {
      console.log('confirmClearArtists called');
      // Clear primary and secondary artists
      this.primaryArtist = null;
      this.secondaryArtists = [];
      // Update Vuex store
      this.$store.commit('SET_PRIMARY_ARTIST', null);
      this.$store.commit('SET_SECONDARY_ARTISTS', []);
      // Undim the wizard background
      this.wizardPaused = false;
      
      if (this.pendingVariousArtistsChange) {
        // Complete the Various Artists selection
        console.log('Proceeding with Various Artists Release');
        
        // For Record Label flow with Various Artists Release, always open
        // the Record Label selector dialog even if a label is already selected
        if (this.selectedAccountType === 'Record Label') {
          console.log('Record Label flow with Various Artists Release - opening Record Label selector');
          // Store the structure in Vuex first
          this.$store.commit('SET_RELEASE_STRUCTURE', this.selectedStructure);
          // Emit the structure selection event
          this.$emit('structure-selected', this.selectedStructure);
          // Reset the pending flag
          this.pendingVariousArtistsChange = false;
          // Open the Record Label selector dialog immediately
          this.openRecordLabelDialog();
          return;
        }
        
        // For non-Record Label flow, complete the selection
        this.$emit('account-type-selected', {
          accountType: this.selectedAccountType,
          structure: this.selectedStructure,
          variousArtists: true,
          // Include the label if one is selected (for Record Label flow)
          label: this.selectedLabel || null
        });
        
        // Reset the pending flag
        this.pendingVariousArtistsChange = false;
        
        // Close the wizard
        this.closeWizard();
      }
    },
    
    /**
     * Handle cancellation of clearing artists when switching to Various Artists Release
     */
    cancelClearArtists() {
      console.log('cancelClearArtists called');
      // Make sure the dialog is closed
      this.$refs.releaseDialogs.clearArtistsDialogVisible = false;
      // Undim the wizard background
      this.wizardPaused = false;
      
      if (this.pendingVariousArtistsChange) {
        // Revert structure selection
        if (this.secondaryArtists && this.secondaryArtists.length > 0) {
          // If we have secondary artists, switch back to Multi-Artist Release
          this.selectedStructure = 'Multi-Artist Release';
          console.log('Reverted to Multi-Artist Release structure after cancellation');
        } else if (this.primaryArtist) {
          // If we only have a primary artist, switch back to Single Artist Release
          this.selectedStructure = 'Single Artist Release';
          console.log('Reverted to Single Artist Release structure after cancellation');
        }
        
        // Update Vuex store with the reverted structure
        this.$store.commit('SET_RELEASE_STRUCTURE', this.selectedStructure);
        
        // Reset the pending flag
        this.pendingVariousArtistsChange = false;
      }
    },
    
    ...mapActions([
      'GET_YOUR_ARTISTS',
      'UNCANCEL_ARTIST_SUBSCRIPTION',
      'RENEW_ARTIST_SUBSCRIPTION'
    ]),
    ...mapMutations([
      'SET_PRIMARY_ARTIST',
      'SET_SECONDARY_ARTISTS'
    ]),
    closeWizard() {
      // If we're in step 1 and have made a temporary selection, revert the previews
      if (this.currentStep === 1 && this.selectedAccountType) {
        console.log('Canceling from step 1, reverting flow type preview');
        // Emit a flow-type-preview-changed event with null to revert to previous state
        this.$emit('flow-type-preview-changed', null);
        
        // Also revert any structure preview if it was active
        if (this.previewedStructure) {
          this.$emit('structure-preview-changed', null);
        }
      }
      
      // Reset wizard state
      this.resetWizard();
      this.dialogVisible = false;
    },
    resetWizard() {
      this.selectedAccountType = null;
      this.selectedStructure = null;
      this.primaryArtist = null;
      this.secondaryArtists = [];
      this.selectedLabel = null;
      this.selectedArtistOption = null;
      this.showCreateAccountBox = false;
      this.currentStep = 1;
      
      // Reset previews
      this.previewedFlowType = null;
      this.previewedStructure = null;
      
      // We don't clear the Vuex store here to avoid breaking synchronization
      // with ReleaseInfo when the wizard is closed but not reset
    },
    selectAccountType(type) {
      // Check if we're switching from Record Label to Artist Account
      const switchingFromLabelToArtist = this.selectedAccountType === 'Record Label' && type === 'Artist Account';
      
      // Check if we have Various Artists selected
      const hadVariousArtistsSelected = this.selectedStructure === 'Various Artists Release';
      
      // Store current account type to detect changes
      const previousAccountType = this.selectedAccountType;
      
      // Update the account type
      this.selectedAccountType = type;
      
      // If switching from Record Label to Artist Account with Various Artists selected,
      // we need to reset the structure since Various Artists is only valid for Record Label flow
      if (switchingFromLabelToArtist && hadVariousArtistsSelected) {
        console.log('Switching from Record Label with Various Artists to Artist Account, resetting structure');
        this.selectedStructure = null;
        this.previewedStructure = null;
        
        // Emit structure reset event
        this.$emit('structure-preview-changed', null);
        
        // Update Vuex store to clear the structure
        this.$store.commit('SET_RELEASE_STRUCTURE', null);
      }
      
      // Emit flow-type-preview-changed event as soon as user selects an account type
      // This ensures guidance lines update immediately without waiting for Next button
      const flowType = type === 'Artist Account' ? 'artistFlow' : 'labelFlow';
      this.previewedFlowType = flowType; // Store locally for tracking
      this.$emit('flow-type-preview-changed', flowType);
      
      // Also immediately emit a structure-preview if one is selected
      if (this.selectedStructure) {
        this.previewedStructure = this.selectedStructure; // Store locally for tracking
        this.$emit('structure-preview-changed', this.selectedStructure);
      }
    },
    selectStructure(structure) {
      // If we're changing the structure, first clear any previous structure preview
      if (this.selectedStructure !== structure) {
        console.log('Changing structure selection from', this.selectedStructure, 'to', structure);
        
        // If we're changing from Various Artists to something else, make sure to reset
        // any Various Artists-specific state
        if (this.selectedStructure === 'Various Artists Release') {
          console.log('Changing from Various Artists Release, special reset required');
          // First null out the structure to reset any structure-specific line state
          this.$emit('structure-preview-changed', null);
          
          // Then, after a short delay, set the new structure
          setTimeout(() => {
            this.selectedStructure = structure;
            this.previewedStructure = structure;
            this.$emit('structure-preview-changed', structure);
          }, 10);
          
          return; // Return early since we're handling this with setTimeout
        }
      }
      
      // Only update the local structure state when selected
      // We'll update the Vuex store when the user clicks Next
      console.log('Structure selected (preview):', structure);
      this.selectedStructure = structure;
      
      // Emit the structure preview immediately to update guidance lines
      this.previewedStructure = structure; // Store locally for tracking
      this.$emit('structure-preview-changed', structure);
    },
    selectArtistOption(option) {
      this.selectedArtistOption = option;
      
      if (option === 'Primary Artist') {
        // Always move to the primary artist selection step when Primary Artist is clicked
        // regardless of whether a primary artist is already selected
        this.currentStep = 5;
        // Load user artists for selection
        this.loadUserArtists(true);
      } else if (option === 'Secondary Artists') {
        // Show the secondary artists selector dialog
        this.showSecondaryArtistsDialog = true;
        this.wizardPaused = true;
      } else if (option === 'Record Label') {
        // Open the Record Label Selector dialog
        this.openRecordLabelDialog();
      }
    },
    
    /**
     * Handle secondary artists selection
     */
    onSecondaryArtistsSelected(selectedArtists) {
      console.log('Wizard - onSecondaryArtistsSelected called with:', selectedArtists);
      
      // Update the secondary artists array
      this.secondaryArtists = [...selectedArtists];
      console.log('Wizard - Updated local secondaryArtists:', this.secondaryArtists);
      
      // Update the store with the selected secondary artists for synchronization with ReleaseInfo
      this.SET_SECONDARY_ARTISTS([...selectedArtists]);
      console.log('Wizard - Called SET_SECONDARY_ARTISTS mutation');
      
      // Close the dialog
      this.showSecondaryArtistsDialog = false;
      this.wizardPaused = false;
      
      // Return to the multi-artist selection screen (step 4) to show both primary and secondary selections
      this.currentStep = 4;
    },
    
    /**
     * Handle dialog closed event from secondary artists selector
     */
    onSecondaryArtistsDialogClosed() {
      // Unpause the wizard when the dialog is closed via the Back button
      this.wizardPaused = false;
      // Close the dialog if it's still open
      this.$nextTick(() => {
        this.showSecondaryArtistsDialog = false;
        
        // If the secondary artists weren't changed, emit a cancel event
        const currentSecondaryArtistsIds = this.secondaryArtists.map(a => a.id).sort().join(',');
        const storeSecondaryArtistsIds = this.storeSecondaryArtists.map(a => a.id).sort().join(',');
        
        if (currentSecondaryArtistsIds === storeSecondaryArtistsIds) {
          console.log('Secondary Artists selection cancelled or unchanged');
          this.$emit('secondary-artists-selection-cancelled');
        }
      });
    },
    
    /**
     * Select the primary artist for both single and multi-artist releases
     * This handles artist selection in both step 3 (single artist) and step 5 (multi-artist)
     */
    selectPrimaryArtist(artist) {
      // Store the artist temporarily for potential use in the warning dialog
      this.tempArtistSelection = artist;
      
      // Check if this artist is already in the secondary artists list
      if (this.secondaryArtists && this.secondaryArtists.length > 0) {
        const artistIndex = this.secondaryArtists.findIndex(secondaryArtist => secondaryArtist.id === artist.id);
        
        // If the artist is found in the secondary artists list, show warning dialog
        if (artistIndex !== -1) {
          console.log('Artist already selected as secondary, showing confirmation dialog:', artist.name);
          // Set the artist name in the ReleaseDialogs component
          this.$refs.releaseDialogs.artistToMove = artist.name;
          this.$refs.releaseDialogs.artistId = artist.id;
          // Show the warning dialog and pause the wizard
          this.$refs.releaseDialogs.artistAlreadySecondaryDialogVisible = true;
          this.wizardPaused = true;
          return; // Don't proceed until user confirms
        }
      }
      
      // If not in secondary artists, proceed with selection directly
      this.completePrimaryArtistSelection(artist);
    },
    
    /**
     * Complete the primary artist selection after checks
     */
    completePrimaryArtistSelection(artist) {
      // Set the primary artist, so we know which artist to renew if needed
      this.primaryArtist = artist;
      
      // Update the store
      this.$store.commit('SET_PRIMARY_ARTIST', artist);
      
      // Check if the artist has subscription issues
      if (this.hasRestrictedStatus(artist)) {
        // Show the subscription renewal dialog
        this.showSubscriptionRenewalDialog = true;
        this.wizardPaused = true;
        return; // Don't proceed with selection until subscription is renewed
      }
      
      // No automatic advancing for either flow - user must click Next/Finish button to continue
      // This creates a more consistent experience between both flows
    },
    
    /**
     * Handle confirmation from the move artist dialog
     */
    handleConfirmMoveArtist() {
      if (!this.tempArtistSelection) return;
      
      // Remove the artist from secondary artists list
      const updatedSecondaryArtists = this.secondaryArtists.filter(a => a.id !== this.tempArtistSelection.id);
      
      // Update both local state and Vuex store
      this.secondaryArtists = updatedSecondaryArtists;
      this.$store.commit('SET_SECONDARY_ARTISTS', updatedSecondaryArtists);
      
      // Unpause the wizard
      this.wizardPaused = false;
      
      // Complete the primary artist selection
      this.completePrimaryArtistSelection(this.tempArtistSelection);
      
      // Clear the temporary selection
      this.tempArtistSelection = null;
    },
    
    /**
     * Handle cancellation from the move artist dialog
     */
    handleCancelMoveArtist() {
      // Clear the temporary selection and unpause the wizard
      this.tempArtistSelection = null;
      this.wizardPaused = false;
    },
    
    /**
     * Clear the selected primary artist
     */
    clearPrimaryArtist(event) {
      // Clear the primary artist selection
      this.primaryArtist = null;
      
      // Clear the main artist in the store
      this.$store.commit('SET_PRIMARY_ARTIST', null);
      
      // Prevent the click from propagating to the parent div
      // which would trigger selectArtistOption
      if (event) {
        event.stopPropagation();
      }
    },
    
    /**
     * Clear the secondary artists selection
     */
    clearSecondaryArtists(event) {
      // Prevent event propagation if this was triggered by a click
      if (event) event.stopPropagation();
      
      // Clear secondary artists from component state
      this.secondaryArtists = [];
      
      // Also update the store
      this.$store.commit('SET_SECONDARY_ARTISTS', []);
      
      // If this was triggered in the Multi-Artist Release flow, move
      // the selection highlight back to the Primary Artist option
      if (this.selectedStructure === 'Multi-Artist Release') {
        this.selectedArtistOption = 'Primary Artist';
      }
    },
    
    /**
     * Open the Record Label Selector dialog
     */
    openRecordLabelDialog() {
      // Make sure structure is set in Vuex store if we're in Various Artists flow
      if (this.selectedStructure === 'Various Artists Release') {
        this.$store.commit('SET_RELEASE_STRUCTURE', this.selectedStructure);
        this.$emit('structure-selected', this.selectedStructure);
      }
      
      // Pause the wizard while the record label selector is open
      this.wizardPaused = true;
      this.showRecordLabelDialog = true;
    },
    
    /**
     * Handle record label selection from the RecordLabelSelector
     * @param {Object} label The selected record label object
     */
    onRecordLabelSelected(label) {
      console.log('Record label selected:', label);
      this.selectedLabel = label;
      
      // Save the label in the store
      this.$store.commit('SET_SELECTED_LABEL', label);
      
      // For Various Artists Release structure, complete the flow immediately
      if (this.selectedStructure === 'Various Artists Release') {
        console.log('Various Artists Release with label selected, completing flow');
        
        // Emit the complete selection event
        this.$emit('account-type-selected', {
          accountType: this.selectedAccountType,
          structure: this.selectedStructure,
          variousArtists: true,
          label: label
        });
        
        // Close the wizard
        this.closeWizard();
        return;
      }
      
      // For other structures, proceed to the next step
      this.nextStep();
    },
    
    /**
     * Handle the closing of the RecordLabelSelector dialog
     */
    onRecordLabelDialogClosed() {
      this.wizardPaused = false;
      this.showRecordLabelDialog = false;
      
      // If we're in the Record Label flow and the label wasn't selected/changed,
      // emit an event to indicate the selection was cancelled
      if (this.selectedAccountType === 'Record Label' && 
          (!this.selectedLabel || (this.SELECTED_LABEL && this.selectedLabel.id === this.SELECTED_LABEL.id))) {
        console.log('Record Label selection cancelled or unchanged');
        this.$emit('record-label-selection-cancelled');
      }
    },
    
    clearSelectedLabel(event) {
      // * Clear the selected record label
      // */
      // Prevent the click from propagating to the parent div
      // which would trigger selectArtistOption
      if (event) {
        event.stopPropagation();
      }
      
      // Clear the selected label
      this.selectedLabel = null;
      
      // Reset the selected option if it was Record Label
      if (this.selectedArtistOption === 'Record Label') {
        this.selectedArtistOption = null;
      }
      
      console.log('Record label cleared');
    },
    
    // Check if the primary artist has an active subscription
    canProceedWithPrimaryArtist() {
      return this.primaryArtist && this.primaryArtist.hasActiveSubscription;
    },
    
    hasRestrictedStatus(account) {
      if (!account) return false;
      
      const rule1 = !account.hasActiveSubscription && !account.subscription;
      const rule2 = !account.hasActiveSubscription && account.subscription && !account.subscription.canActivate;
      
      return rule1 || rule2;
    },
    
    hasExpiresStatus(account) {
      if (!account) return false;
      return account.subscription && account.subscription.status && account.subscription.status === 'past_due';
    },
    
    // Handle subscription renewal cancellation
    onRenewalCancel() {
      // Explicitly set the dialog to false
      this.showSubscriptionRenewalDialog = false;
      
      // Resume the wizard flow
      this.wizardPaused = false;
      
      // Clear the primary artist selection to prevent proceeding with an expired account
      if (this.primaryArtist && this.hasRestrictedStatus(this.primaryArtist)) {
        this.primaryArtist = null;
      }
    },
    
    // Handle successful subscription renewal
    onRenewalSuccess(renewedAccount) {
      console.log('Subscription renewal successful, keeping wizard open');
      
      // Update the primary artist with the renewed account data
      this.primaryArtist = renewedAccount;
      
      // Explicitly set the dialog to false
      this.showSubscriptionRenewalDialog = false;
      
      // Resume the wizard flow
      this.wizardPaused = false;
      
      // Force refresh the user's artists list to ensure we have the latest data
      this.loadUserArtists(true);
      
      // If we're in step 3, proceed with the renewed account
      if (this.currentStep === 3) {
        // Set the artist as the release main artist
        this.$store.commit('SET_PRIMARY_ARTIST', this.primaryArtist);
        
        // We no longer automatically close the wizard or emit the selection
        // This allows the user to continue using the wizard after renewal
      }
    },
    /**
     * Show the artist creation dialog and pause the wizard
     */
    openArtistCreationDialog() {
      this.showArtistCreationDialog = true;
      this.wizardPaused = true;
    },
    
    /**
     * Handle cancellation of the artist creation dialog
     */
    onArtistCreationCancel() {
      // Resume the wizard flow
      this.wizardPaused = false;
    },
    
    /**
     * Handle the newly created artist from the ArtistCreationForm component
     * @param {string} artistName - The name of the newly created artist
     */
    onArtistCreated(artistName) {
      console.log(`Artist created: ${artistName}`);
      
      // Resume the wizard flow
      this.wizardPaused = false;
      
      // Force refresh the user's artists list to include the new artist
      this.loadUserArtists(true);
      
      // Show a success message or toast if needed
      this.$nextTick(() => {
        // Find the newly created artist in the refreshed list
        if (this.userArtists && this.userArtists.length) {
          const newArtist = this.userArtists.find(artist => artist.name === artistName);
          if (newArtist) {
            // Select the newly created artist
            this.selectPrimaryArtist(newArtist);
          }
        }
      });
    },
    /**
     * Restore the wizard state from previous selections
     */
    restoreWizardState() {
      console.log('Restoring wizard state');
      
      // Check if we have previously selected data in the store
      const storedArtist = this.PRIMARY_ARTIST;
      const storedStructure = this.RELEASE_STRUCTURE;
      const storedLabel = this.SELECTED_LABEL;
      
      // First check for structure in the Vuex store
      if (storedStructure) {
        console.log('Found previously selected structure in Vuex store:', storedStructure);
        this.selectedStructure = storedStructure;
      }
      
      // Check for label in the Vuex store
      if (storedLabel) {
        console.log('Found previously selected label:', storedLabel);
        this.selectedLabel = storedLabel;
        
        // If we have a label but no account type yet, set it to Record Label
        if (!this.selectedAccountType) {
          this.selectedAccountType = 'Record Label';
        }
      }
      
      // Then check for artist in the Vuex store
      if (storedArtist) {
        console.log('Found previously selected artist:', storedArtist);
        
        // Always set the primaryArtist regardless of structure
        this.primaryArtist = storedArtist;
        
        // If we don't have an account type yet, set it to Artist Account
        if (!this.selectedAccountType) {
          this.selectedAccountType = 'Artist Account';
        }
        
        // If we don't have a structure yet, determine it based on secondary artists
        if (!this.selectedStructure) {
          if (this.storeSecondaryArtists && this.storeSecondaryArtists.length > 0) {
            // If we have secondary artists, it must be a multi-artist release
            this.selectedStructure = 'Multi-Artist Release';
            // Also update the Vuex store
            this.$store.commit('SET_RELEASE_STRUCTURE', 'Multi-Artist Release');
          } else {
            // Otherwise, it's a single artist release
            this.selectedStructure = 'Single Artist Release';
            // Also update the Vuex store
            this.$store.commit('SET_RELEASE_STRUCTURE', 'Single Artist Release');
          }
        }
        
        // Always sync secondary artists
        if (this.storeSecondaryArtists && this.storeSecondaryArtists.length > 0) {
          this.secondaryArtists = [...this.storeSecondaryArtists];
        }
      }
      
      // Always start at step 1 regardless of previous selections
      this.currentStep = 1;
    },
    
    loadUserArtists() {
      console.log('Loading user artists - always fetching fresh data');
      
      // Set loading flag
      this.isLoadingArtists = true;
      
      // Always fetch fresh data, never use cache
      this.userArtists = []; // Clear current artists
      
      // Get user's artists with consistent parameters matching ReleaseInfo implementation
      this.GET_YOUR_ARTISTS({
        owner: this.userID,
        deletedAt: false,
        'order[createdAt]': 'desc'
      }).then(() => {
        console.log('Received artists from API');
        
        // Process the response based on its structure
        if (this.YOUR_ARTISTS) {
          let allArtists = [];
          
          if (this.YOUR_ARTISTS['hydra:member']) {
            // API returned paginated response
            allArtists = this.YOUR_ARTISTS['hydra:member'];
          } else if (Array.isArray(this.YOUR_ARTISTS)) {
            // API returned direct array
            allArtists = this.YOUR_ARTISTS;
          } else {
            console.log('Unexpected YOUR_ARTISTS structure');
            this.userArtists = [];
            return;
          }

          // Check the first artist to see its structure
          if (allArtists.length > 0) {
            const firstArtist = allArtists[0];
            console.log('Artist structure example:', JSON.stringify({
              id: firstArtist.id,
              name: firstArtist.name,
              ownerInfo: firstArtist.owner ? `Has owner prop: ID=${firstArtist.owner.id}` : 'No owner',
              userInfo: firstArtist.user ? 'Has user prop' : 'No user',
              userIdInfo: firstArtist.userId ? 'Has userId prop' : 'No userId'
            }));
          }
          
          // Include all user's artists in the list
          console.log('Processing all user artists...');
          this.userArtists = allArtists;
          
          // Sort artists - non-expired (active) subscriptions first
          this.userArtists.sort((a, b) => {
            // Active subscriptions first
            if (a.hasActiveSubscription === true && b.hasActiveSubscription !== true) return -1;
            if (a.hasActiveSubscription !== true && b.hasActiveSubscription === true) return 1;
            // Then by name
            return a.name?.localeCompare(b.name) || 0;
          });
          
          console.log('Found user artists:', this.userArtists.length);
        } else {
          console.log('YOUR_ARTISTS is null or undefined');
          this.userArtists = [];
        }
        // Reset loading flag after successful API call
        this.isLoadingArtists = false;
      }).catch(err => {
        console.error('Error loading artists:', err);
        this.userArtists = [];
        // Reset loading flag on error
        this.isLoadingArtists = false;
      });
    },
    previousStep() {
      // If we're going back from structure selection to account type selection
      if (this.currentStep === 2) {
        // Reset any structure selection preview
        console.log('Going back from structure selection, resetting structure preview');
        this.previewedStructure = null;
        this.$emit('structure-preview-changed', null);
      }
      
      // Handle specific step transitions
      if (this.currentStep === 4) {
        // From primary/secondary artist selection, go back to structure selection
        this.currentStep = 2;
      } else if (this.currentStep === 5) {
        // Check if we're in the Record Label flow or the Artist Account flow
        if (this.selectedAccountType === 'Record Label' && this.selectedStructure === 'Single Artist Release') {
          // From primary artist selection in Record Label flow, go back to step 6
          this.currentStep = 6;
        } else {
          // From primary artist selection in Artist Account flow, go back to step 4
          this.currentStep = 4;
        }
      } else if (this.currentStep === 6) {
        // From Record Label + Artist selection, go back to structure selection
        this.currentStep = 2;
      } else if (this.currentStep > 1) {
        // Default behavior - go back one step
        this.currentStep -= 1;
      }
      
      // Log the navigation for debugging
      console.log('Navigated to step:', this.currentStep);
    },
    nextStep() {
      if (this.currentStep === 1 && this.selectedAccountType) {
        // If user is proceeding with Artist Account and has a label selected, show warning
        if (this.selectedAccountType === 'Artist Account' && this.selectedLabel) {
          // Set the label name in the dialog
          this.$refs.releaseDialogs.labelToRemove = this.selectedLabel.title || 'Selected Label';
          // Show the clear label confirmation dialog
          this.$refs.releaseDialogs.clearLabelDialogVisible = true;
          // Set flag to indicate we're waiting for confirmation
          this.pendingArtistAccountNavigation = true;
          // Dim the wizard background
          this.wizardPaused = true;
          // Log for debugging
          console.log('Showing clear label dialog with label:', this.selectedLabel.title);
          // Don't proceed yet
          return;
        }
        
        // Move to structure selection for both Artist Account and Record Label
        this.currentStep = 2;
      } else if (this.currentStep === 2 && this.selectedStructure) {
        // Now that the user has clicked Next, store the structure in the Vuex store
        // and emit the structure selection event to ReleaseInfo
        console.log('User confirmed structure selection:', this.selectedStructure);
        
        // Store the structure in the Vuex store for consistency across components
        this.$store.commit('SET_RELEASE_STRUCTURE', this.selectedStructure);
        
        // Emit the event to update the ReleaseInfo component
        this.$emit('structure-selected', this.selectedStructure);
        
        if (this.selectedStructure === 'Single Artist Release') {
          // If switching from Multi-Artist to Single Artist, clear secondary artists
          // We only do this when the user clicks Next, not when they just select the option
          if (this.secondaryArtists && this.secondaryArtists.length > 0) {
            console.log('Secondary artists detected, showing confirmation dialog');
            // Set the secondary artists count in the dialog
            this.$refs.releaseDialogs.secondaryArtistsCount = this.secondaryArtists.length;
            // Show the clear secondary artists confirmation dialog
            this.$refs.releaseDialogs.clearSecondaryArtistsDialogVisible = true;
            // Set flag to indicate we're waiting for confirmation
            this.pendingStructureChange = true;
            // Dim the wizard background
            this.wizardPaused = true;
            // Log for debugging
            console.log('Showing clear secondary artists dialog');
            // Don't proceed yet
            return;
          }
          
          // For Record Label flow with Single Artist Release, show label/artist selection
          if (this.selectedAccountType === 'Record Label') {
            // Move to Record Label + Artist selection for Record Label flow
            this.currentStep = 6; // New step for Record Label flow
          } else {
            // Move to artist selection for Artist Account flow
            this.currentStep = 3;
          }
        } else if (this.selectedStructure === 'Multi-Artist Release') {
          // Move to multi-artist selection options
          this.currentStep = 4;
        } else if (this.selectedStructure === 'Various Artists Release') {
          // Check if there are any artists to clear before proceeding
          let hasArtistsToRemove = false;
          let artistsDescription = '';
          
          // Check for primary artist
          if (this.primaryArtist) {
            hasArtistsToRemove = true;
            artistsDescription = `<span>Primary Artist:</span> <span class="artist-name">${this.primaryArtist.name}</span>`;
          }
          
          // Check for secondary artists
          if (this.secondaryArtists && this.secondaryArtists.length > 0) {
            hasArtistsToRemove = true;
            if (artistsDescription) {
              artistsDescription += ` <span>and</span> <span class="artist-name">${this.secondaryArtists.length} Secondary Artist${this.secondaryArtists.length !== 1 ? 's' : ''}</span>`;
            } else {
              artistsDescription = `<span class="artist-name">${this.secondaryArtists.length} Secondary Artist${this.secondaryArtists.length !== 1 ? 's' : ''}</span>`;
            }
          }
          
          // If there are artists to clear, show the warning dialog
          if (hasArtistsToRemove) {
            console.log('Artists detected, showing confirmation dialog before switching to Various Artists');
            // Set the artists to clear description in the dialog
            this.$refs.releaseDialogs.artistsToClear = artistsDescription;
            // Show the clear artists confirmation dialog
            this.$refs.releaseDialogs.clearArtistsDialogVisible = true;
            // Set flag to indicate we're waiting for confirmation
            this.pendingVariousArtistsChange = true;
            // Dim the wizard background
            this.wizardPaused = true;
            // Log for debugging
            console.log('Showing clear artists dialog');
            // Don't proceed yet
            return;
          }
          
          // If no artists to clear, proceed directly
          console.log('Completing Various Artists Release selection');
          
          // For Record Label flow with Various Artists Release, always open the Record Label selector
          if (this.selectedAccountType === 'Record Label') {
            console.log('Record Label flow with Various Artists Release - opening Record Label selector');
            // Store the structure in Vuex first
            this.$store.commit('SET_RELEASE_STRUCTURE', this.selectedStructure);
            // Emit the structure selection event
            this.$emit('structure-selected', this.selectedStructure);
            // Open the Record Label selector dialog immediately
            this.openRecordLabelDialog();
            return;
          }
          
          // For non-Record Label flow, complete the selection
          this.$emit('account-type-selected', {
            accountType: this.selectedAccountType,
            structure: this.selectedStructure,
            variousArtists: true,
            // Include the label if one is selected (for Record Label flow)
            label: this.selectedLabel || null
          });
          
          // Close the wizard
          this.closeWizard();
        }
      } else if (this.currentStep === 3 && this.primaryArtist) {
        // User has selected a primary artist in single artist flow and clicked Finish
        // Always check if the primary artist has an active subscription
        // This ensures the warning appears even if user previously dismissed it
        if (this.canProceedWithPrimaryArtist()) {
          // Set the artist as the release main artist
          this.$store.commit('SET_PRIMARY_ARTIST', this.primaryArtist);
          
          // Emit the complete selection with artist and subscription status
          this.$emit('account-type-selected', {
            accountType: this.selectedAccountType,
            structure: this.selectedStructure,
            artist: {
              ...this.primaryArtist,
              hasActiveSubscription: this.primaryArtist.hasActiveSubscription || false
            }
          });
          
          this.closeWizard();
        } else {
          // Show the subscription renewal dialog for expired subscription
          this.showSubscriptionRenewalDialog = true;
          this.wizardPaused = true;
        }
      } else if (this.currentStep === 4 && this.selectedStructure === 'Multi-Artist Release') {
        // Multi-artist flow - handle Finish button or navigation to artist selection
        // Case 1: Both primary and secondary artists are selected - this is the Finish button case
        if (this.primaryArtist && this.secondaryArtists && this.secondaryArtists.length > 0 && 
            (this.selectedAccountType !== 'Record Label' || this.selectedLabel)) {
          console.log('Completing multi-artist selection with both primary and secondary artists');
          
          // Store the primary artist in the Vuex store
          this.$store.commit('SET_PRIMARY_ARTIST', this.primaryArtist);
          
          // Store the label in the Vuex store if we're in Record Label flow
          if (this.selectedAccountType === 'Record Label' && this.selectedLabel && 
              this.$store._mutations['SET_SELECTED_LABEL']) {
            this.$store.commit('SET_SELECTED_LABEL', this.selectedLabel);
          }
          
          // Create the selection object
          const selection = {
            accountType: this.selectedAccountType,
            structure: this.selectedStructure,
            primaryArtist: {
              ...this.primaryArtist,
              hasActiveSubscription: this.primaryArtist.hasActiveSubscription || false
            },
            secondaryArtists: this.secondaryArtists.map(artist => ({
              ...artist,
              hasActiveSubscription: artist.hasActiveSubscription || false
            }))
          };
          
          // Add the label if we're in the Record Label flow
          if (this.selectedAccountType === 'Record Label' && this.selectedLabel) {
            selection.label = this.selectedLabel;
          }
          
          // Emit the complete selection with all necessary properties
          this.$emit('account-type-selected', selection);
          
          // Close the wizard
          this.closeWizard();
          return;
        }
        
        // Case 2: Handle the multi-artist selection options based on which option is selected
        if (this.selectedArtistOption === 'Primary Artist') {
          // If we don't have a primary artist selected yet, go to the artist selection step
          if (!this.primaryArtist) {
            this.currentStep = 5; // Use step 5 for multi-artist flow primary artist selection
            // Load user artists for selection
            this.loadUserArtists(true);
          } else {
            // User has selected Primary Artist option, has a primary artist selected, and clicked Finish
            // Store the primary artist in the Vuex store
            this.$store.commit('SET_PRIMARY_ARTIST', this.primaryArtist);
            
            // Emit the complete selection with subscription status
            this.$emit('account-type-selected', {
              accountType: this.selectedAccountType,
              structure: this.selectedStructure,
              artistOption: this.selectedArtistOption,
              primaryArtist: {
                ...this.primaryArtist,
                hasActiveSubscription: this.primaryArtist.hasActiveSubscription || false
              },
              secondaryArtists: this.secondaryArtists.map(artist => ({
                ...artist,
                hasActiveSubscription: artist.hasActiveSubscription || false
              }))
            });
            
            this.closeWizard();
          }
        } else if (this.selectedArtistOption === 'Secondary Artists') {
          // User has selected Secondary Artists option and clicked Finish
          // Secondary artists are optional, so we can complete regardless of whether any are selected
          
          // If they've also selected a primary artist, make sure it's stored
          if (this.primaryArtist) {
            this.$store.commit('SET_PRIMARY_ARTIST', this.primaryArtist);
          }
          
          // Emit the complete selection with subscription status
          this.$emit('account-type-selected', {
            accountType: this.selectedAccountType,
            structure: this.selectedStructure,
            artistOption: this.selectedArtistOption,
            primaryArtist: {
              ...this.primaryArtist,
              hasActiveSubscription: this.primaryArtist.hasActiveSubscription || false
            },
            secondaryArtists: this.secondaryArtists.map(artist => ({
              ...artist,
              hasActiveSubscription: artist.hasActiveSubscription || false
            }))
          });
          
          this.closeWizard();
        }
      } else if (this.currentStep === 5 && this.primaryArtist) {
        // When user clicks Next on the primary artist selection screen
        // Set the primary artist as the main artist in the store
        this.$store.commit('SET_PRIMARY_ARTIST', this.primaryArtist);
        
        // Check if we're in the Record Label flow or the Artist Account flow
        if (this.selectedAccountType === 'Record Label' && this.selectedStructure === 'Single Artist Release') {
          // For Record Label flow with Single Artist Release, return to step 6
          this.currentStep = 6;
        } else {
          // For Artist Account flow, return to step 4
          this.currentStep = 4;
        }
      } else if (this.currentStep === 6) {
        // Record Label flow with either Single Artist Release or Various Artists Release
        
        // Separate handling for Various Artists Release structure
        if (this.selectedStructure === 'Various Artists Release') {
          // For Various Artists, we only need a label to be selected
          if (this.selectedLabel) {
            console.log('Completing Record Label flow with Various Artists Release');
            
            // Store the label in the Vuex store (if there's a SET_SELECTED_LABEL mutation)
            if (this.$store._mutations['SET_SELECTED_LABEL']) {
              this.$store.commit('SET_SELECTED_LABEL', this.selectedLabel);
            }
            
            // Emit the complete selection with all necessary properties
            this.$emit('account-type-selected', {
              accountType: this.selectedAccountType,
              structure: this.selectedStructure,
              label: this.selectedLabel,
              variousArtists: true
            });
            
            // Close the wizard
            this.closeWizard();
            return;
          } else if (this.selectedArtistOption === 'Record Label') {
            // Open the Record Label Selector dialog if user clicked on the Record Label option
            this.openRecordLabelDialog();
            return;
          }
        }
        // Original code for Single Artist Release structure
        else if (this.selectedStructure === 'Single Artist Release') {
          // Case 1: Both label and primary artist are selected - this is the Finish button case
          if (this.selectedLabel && this.primaryArtist) {
            console.log('Completing Record Label flow with Single Artist Release');
            
            // Store the primary artist in the Vuex store
            this.$store.commit('SET_PRIMARY_ARTIST', this.primaryArtist);
            
            // Store the label in the Vuex store (if there's a SET_SELECTED_LABEL mutation)
            if (this.$store._mutations['SET_SELECTED_LABEL']) {
              this.$store.commit('SET_SELECTED_LABEL', this.selectedLabel);
            }
            
            // Emit the complete selection with all necessary properties
            this.$emit('account-type-selected', {
              accountType: this.selectedAccountType,
              structure: this.selectedStructure,
              label: this.selectedLabel,
              primaryArtist: {
                ...this.primaryArtist,
                hasActiveSubscription: this.primaryArtist.hasActiveSubscription || false
              }
            });
            
            // Close the wizard
            this.closeWizard();
            return;
          }
        }
        
        // Case 2: Handle the label/artist selection options based on which option is selected
        if (this.selectedArtistOption === 'Record Label') {
          // User has selected Record Label option and clicked Next
          // Open the Record Label Selector dialog
          this.openRecordLabelDialog();
        } else if (this.selectedArtistOption === 'Primary Artist' && this.selectedStructure === 'Single Artist Release') {
          // User has selected Primary Artist option and clicked Next
          // If we don't have a primary artist selected yet, go to the artist selection step
          if (!this.primaryArtist) {
            // Use step 5 for artist selection (same as multi-artist flow)
            this.currentStep = 5;
            // Load user artists for selection
            this.loadUserArtists(true);
          }
        }
      }
    }
  }
}
</script>

<style scoped>
.dimmed-dialog {
  position: relative;
}

.dialog-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 10;
  border-radius: inherit;
}
</style>

<!-- Styles moved to dedicated accountWizard.scss file -->
